import { Box, Button, Input } from '@mui/material'
import StandardStateSelector from 'components/utility/StandardStateSelector'
import React from 'react'
import { GDriveSpreadsheet } from '../googleApis/types'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { googleUserAtom } from 'infrastructure/googleDrive'
import { getStateStandardsAsMap } from 'components/tree/standardUtilities'
import { Standard } from 'shared/types'
import { Table } from 'shared/ui/Table/Table'
import { createLeaves, NodeAndStandards, processSpreadsheet } from './utilities'
import { INode } from 'shared/types/tree'
import { createIdMap } from 'components/version_mapping/utility'
import styled from '@emotion/styled'

export function ImportStandards({ tree }: { tree: INode }) {
  const idMap = createIdMap(tree)

  const [standardState, setStandardState] = React.useState<string>('')
  const [fileId, setFileId] = React.useState<string>()
  const [loadingFile, setLoadingFile] = React.useState(false)
  const [loadingStandards, setLoadingStandards] = React.useState(false)
  const [adding, setAdding] = React.useState(false)
  const [added, setAdded] = React.useState(false)
  const [stateStandardsMap, setStateStandardsMap] = React.useState<{
    [key: string]: Standard
  }>({})
  const [spreadsheet, setSpreadsheet] = React.useState<GDriveSpreadsheet>()
  const [nodesAndStandards, setNodesAndStandards] = React.useState<
    NodeAndStandards[]
  >([])
  const uniqueIssues = Array.from(
    new Set(nodesAndStandards.flatMap((ns) => ns.issues)),
  )
  const nodesWithNoIssues = nodesAndStandards.filter(
    (ns) => ns.issues.length === 0,
  )
  const nodesWithIssues = nodesAndStandards.filter((ns) => ns.issues.length > 0)
  const uniqueStandards = Array.from(
    new Set(nodesWithNoIssues.flatMap((ns) => ns.standards)),
  )

  const guser = useAtomValue(googleUserAtom)

  React.useEffect(() => {
    if (!fileId) return
    if (!guser.credential?.accessToken) return
    setLoadingFile(true)

    GDriveSpreadsheet.create(guser.credential?.accessToken, fileId).then(
      (s) => {
        setSpreadsheet(s)
        setLoadingFile(false)
      },
    )
  }, [fileId])

  React.useEffect(() => {
    if (!standardState) return
    getStateStandardsAsMap(standardState).then((sm) => {
      setStateStandardsMap(sm)
    })
  }, [standardState])

  React.useEffect(() => {
    if (!spreadsheet) return
    setNodesAndStandards(
      processSpreadsheet(spreadsheet, stateStandardsMap, idMap),
    )
  }, [stateStandardsMap, spreadsheet])

  return (
    <Box>
      <h2>Import</h2>
      <StandardStateSelector
        handleChange={(ss) => {
          setStandardState(ss)
          setLoadingStandards(true)
          getStateStandardsAsMap(ss).then((sm) => {
            setStateStandardsMap(sm)
            setLoadingStandards(false)
          })
        }}
        currentSelection={standardState}
      />

      <Input
        placeholder="Google Drive File Id"
        value={fileId}
        onChange={(e) => setFileId(e.target.value)}
      />

      {loadingFile && <Box>Loading File...</Box>}
      {loadingStandards && <Box>Loading Standards...</Box>}

      <SummaryBox>
        Adding{' '}
        {nodesWithNoIssues.reduce((acc, ns) => acc + ns.standards.length, 0)}{' '}
        standards to{' '}
        {nodesWithNoIssues.filter((ns) => ns.standards.length > 0).length}{' '}
        nodes.
      </SummaryBox>
      <SummaryBox>Adding {uniqueStandards.length} unique standards.</SummaryBox>

      <SummaryBox>
        {uniqueIssues.length} unique issues found:
        <ul>
          {uniqueIssues.map((issue) => (
            <li>{issue}</li>
          ))}
        </ul>
      </SummaryBox>

      <Table
        title="Nodes With Issues"
        columns={issuesColumns}
        items={nodesWithIssues}
      />

      <Table
        title="Nodes With No Issues"
        toolBarItems={[
          <Button
            disabled={adding || added}
            onClick={() => {
              setAdding(true)
              createLeaves(tree, nodesWithNoIssues).then(() => {
                setAdding(false)
                setAdded(true)
              })
            }}
          >
            {adding ? 'Adding...' : added ? 'Added' : 'Add'}
          </Button>,
          <Button
            onClick={() => {
              setFileId('')
              setNodesAndStandards([])
            }}
          >
            Cancel
          </Button>,
        ]}
        columns={infoColumns}
        items={nodesWithNoIssues}
      />
    </Box>
  )
}

const issuesColumns = [
  {
    title: <strong>Node</strong>,
    width: 100,
    rowContent(a: NodeAndStandards) {
      return (
        <a href={a.linkToNode} target="_blank">
          {a.nodeTitle}
        </a>
      )
    },
  },
  {
    title: <strong>Issues</strong>,
    width: 100,
    rowContent(a: NodeAndStandards) {
      return (
        <ul>
          {a.issues.map((i) => (
            <li>{i}</li>
          ))}
        </ul>
      )
    },
  },
]

const infoColumns = [
  {
    title: <strong>Node</strong>,
    width: 100,
    rowContent(a: NodeAndStandards) {
      return (
        <a href={a.linkToNode} target="_blank">
          {a.nodeTitle}
        </a>
      )
    },
  },
  {
    title: <strong>Standards To Be Added</strong>,
    width: 100,
    rowContent(a: NodeAndStandards) {
      return (
        <ul>
          {a.standards.map((s) => (
            <li>{s.standardName}</li>
          ))}
        </ul>
      )
    },
  },
]

const SummaryBox = styled(Box)(() => ({
  padding: '10px',
}))
